import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Globe,
  Loader2,
  CheckCircle2,
  XCircle,
  Activity,
  Wifi,
  WifiOff,
  Shield,
  Zap,
  AlertTriangle,
} from "lucide-react";

interface DiagnosticCheck {
  ok: boolean;
  status?: number;
  error?: string;
  data?: any;
}

interface DiagnosticResult {
  timestamp: string;
  vps_url: string;
  overall: string;
  checks: {
    vps_health?: DiagnosticCheck;
    dataimpulse_proxy?: DiagnosticCheck;
    binance_api?: DiagnosticCheck;
  };
}

const AdminDataImpulse = () => {
  const [loading, setLoading] = useState(false);
  const [proxyLoading, setProxyLoading] = useState(false);
  const [result, setResult] = useState<DiagnosticResult | null>(null);
  const [proxyResult, setProxyResult] = useState<any>(null);

  const runFullDiagnostics = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await supabase.functions.invoke("proxy-diagnostics", {
        body: { action: "full-diagnostics" },
      });
      if (res.error) throw new Error(res.error.message);
      if (res.data?.success) {
        setResult(res.data);
        toast.success("تم التشخيص بنجاح");
      } else {
        toast.error(res.data?.error || "فشل التشخيص");
      }
    } catch (e: any) {
      toast.error("خطأ: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const testProxy = async () => {
    setProxyLoading(true);
    setProxyResult(null);
    try {
      const res = await supabase.functions.invoke("proxy-diagnostics", {
        body: { action: "test-proxy" },
      });
      if (res.error) throw new Error(res.error.message);
      setProxyResult(res.data);
      if (res.data?.success) {
        toast.success("✅ DataImpulse Proxy يعمل");
      } else {
        toast.error("❌ فشل اختبار البروكسي");
      }
    } catch (e: any) {
      toast.error("خطأ: " + e.message);
      setProxyResult({ success: false, error: e.message });
    } finally {
      setProxyLoading(false);
    }
  };

  const CheckIcon = ({ ok }: { ok: boolean }) =>
    ok ? (
      <CheckCircle2 className="w-5 h-5 text-green-400" />
    ) : (
      <XCircle className="w-5 h-5 text-red-400" />
    );

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Globe className="w-5 h-5 text-primary" />
            إدارة DataImpulse Proxy
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={testProxy}
              disabled={proxyLoading}
            >
              {proxyLoading ? (
                <Loader2 className="w-4 h-4 animate-spin ml-1" />
              ) : (
                <Wifi className="w-4 h-4 ml-1" />
              )}
              اختبار البروكسي
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={runFullDiagnostics}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin ml-1" />
              ) : (
                <Activity className="w-4 h-4 ml-1" />
              )}
              تشخيص كامل
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Architecture Diagram */}
        <div className="p-4 rounded-lg border border-border/50 bg-secondary/20">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            مسار الاتصال
          </h3>
          <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap justify-center">
            <Badge variant="outline" className="bg-primary/10">Edge Function</Badge>
            <span>→</span>
            <Badge variant="outline" className="bg-yellow-500/10 text-yellow-400">Contabo VPS</Badge>
            <span>→</span>
            <Badge variant="outline" className="bg-green-500/10 text-green-400">DataImpulse Proxy</Badge>
            <span>→</span>
            <Badge variant="outline" className="bg-blue-500/10 text-blue-400">Binance API</Badge>
          </div>
        </div>

        {/* Proxy Test Result */}
        {proxyResult && (
          <div
            className={`p-3 rounded-lg border ${
              proxyResult.success
                ? "bg-green-500/10 border-green-500/30"
                : "bg-red-500/10 border-red-500/30"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              {proxyResult.success ? (
                <Wifi className="w-5 h-5 text-green-400" />
              ) : (
                <WifiOff className="w-5 h-5 text-red-400" />
              )}
              <span className="text-sm font-semibold">
                {proxyResult.success
                  ? "✅ البروكسي يعمل بشكل سليم"
                  : "❌ فشل اختبار البروكسي"}
              </span>
            </div>
            {proxyResult.data && (
              <pre className="text-xs bg-black/30 p-2 rounded overflow-auto max-h-40 mt-2 direction-ltr text-left">
                {typeof proxyResult.data === "string"
                  ? proxyResult.data
                  : JSON.stringify(proxyResult.data, null, 2)}
              </pre>
            )}
            {proxyResult.error && (
              <p className="text-xs text-red-400 mt-1">{proxyResult.error}</p>
            )}
          </div>
        )}

        {/* Full Diagnostics Result */}
        {result && (
          <div className="space-y-3">
            {/* Overall Status */}
            <div
              className={`p-3 rounded-lg border ${
                result.overall === "healthy"
                  ? "bg-green-500/10 border-green-500/30"
                  : "bg-yellow-500/10 border-yellow-500/30"
              }`}
            >
              <div className="flex items-center gap-2">
                {result.overall === "healthy" ? (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                )}
                <span className="text-sm font-semibold">
                  {result.overall === "healthy"
                    ? "✅ جميع الأنظمة تعمل بشكل سليم"
                    : "⚠️ توجد مشاكل في بعض الأنظمة"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {result.timestamp} | {result.vps_url}
              </p>
            </div>

            {/* Individual Checks */}
            <div className="grid gap-3">
              {/* VPS Health */}
              {result.checks.vps_health && (
                <div className="p-3 rounded-lg border border-border/50 bg-secondary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckIcon ok={result.checks.vps_health.ok} />
                    <span className="text-sm font-medium">
                      VPS Server Health
                    </span>
                    <Badge
                      variant="outline"
                      className={
                        result.checks.vps_health.ok
                          ? "text-green-400"
                          : "text-red-400"
                      }
                    >
                      {result.checks.vps_health.status || "N/A"}
                    </Badge>
                  </div>
                  {result.checks.vps_health.data && (
                    <pre className="text-xs bg-black/30 p-2 rounded overflow-auto max-h-32 direction-ltr text-left">
                      {typeof result.checks.vps_health.data === "string"
                        ? result.checks.vps_health.data
                        : JSON.stringify(result.checks.vps_health.data, null, 2)}
                    </pre>
                  )}
                  {result.checks.vps_health.error && (
                    <p className="text-xs text-red-400 mt-1">
                      {result.checks.vps_health.error}
                    </p>
                  )}
                </div>
              )}

              {/* DataImpulse Proxy */}
              {result.checks.dataimpulse_proxy && (
                <div className="p-3 rounded-lg border border-border/50 bg-secondary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckIcon ok={result.checks.dataimpulse_proxy.ok} />
                    <span className="text-sm font-medium">
                      DataImpulse Residential Proxy
                    </span>
                    <Badge
                      variant="outline"
                      className={
                        result.checks.dataimpulse_proxy.ok
                          ? "text-green-400"
                          : "text-red-400"
                      }
                    >
                      {result.checks.dataimpulse_proxy.status || "N/A"}
                    </Badge>
                  </div>
                  {result.checks.dataimpulse_proxy.data && (
                    <pre className="text-xs bg-black/30 p-2 rounded overflow-auto max-h-32 direction-ltr text-left">
                      {typeof result.checks.dataimpulse_proxy.data === "string"
                        ? result.checks.dataimpulse_proxy.data
                        : JSON.stringify(
                            result.checks.dataimpulse_proxy.data,
                            null,
                            2
                          )}
                    </pre>
                  )}
                  {result.checks.dataimpulse_proxy.error && (
                    <p className="text-xs text-red-400 mt-1">
                      {result.checks.dataimpulse_proxy.error}
                    </p>
                  )}
                </div>
              )}

              {/* Binance API */}
              {result.checks.binance_api && (
                <div className="p-3 rounded-lg border border-border/50 bg-secondary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckIcon ok={result.checks.binance_api.ok} />
                    <span className="text-sm font-medium">
                      Binance API via Proxy
                    </span>
                    <Badge
                      variant="outline"
                      className={
                        result.checks.binance_api.ok
                          ? "text-green-400"
                          : "text-red-400"
                      }
                    >
                      {result.checks.binance_api.status || "N/A"}
                    </Badge>
                  </div>
                  {result.checks.binance_api.data && (
                    <pre className="text-xs bg-black/30 p-2 rounded overflow-auto max-h-32 direction-ltr text-left">
                      {typeof result.checks.binance_api.data === "string"
                        ? result.checks.binance_api.data
                        : JSON.stringify(
                            result.checks.binance_api.data,
                            null,
                            2
                          )}
                    </pre>
                  )}
                  {result.checks.binance_api.error && (
                    <p className="text-xs text-red-400 mt-1">
                      {result.checks.binance_api.error}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* No data yet */}
        {!result && !proxyResult && (
          <div className="text-center py-8 text-muted-foreground">
            <Globe className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="mb-2">اضغط "تشخيص كامل" لفحص مسار الاتصال بالكامل</p>
            <p className="text-xs">
              Edge Function → Contabo VPS → DataImpulse → Binance
            </p>
          </div>
        )}

        {/* Setup Instructions */}
        <div className="p-4 rounded-lg border border-border/50 bg-secondary/20">
          <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-400" />
            إعداد سريع على Contabo
          </h3>
          <div className="text-xs text-muted-foreground space-y-1 direction-ltr text-left font-mono">
            <p># 1. Upload files to VPS</p>
            <p>scp server.js package.json setup.sh user@VPS_IP:/opt/vps-proxy/</p>
            <p># 2. Run setup</p>
            <p>ssh user@VPS_IP "cd /opt/vps-proxy && chmod +x setup.sh && ./setup.sh"</p>
            <p># 3. Verify</p>
            <p>curl http://VPS_IP:823/health</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminDataImpulse;
